<template>
	<div class="position-relative">
		<div class="dataset-buttons">
			<b-button 
				v-for="property in availableProperties" 
				:key="property" 
				@click="setDataset(property)" 
				:variant="currentDataset == property ? 'outline-primary':'outline-secondary'" 
				class="mr-2"
				size="sm"
			>
				{{ property }}
			</b-button>
		</div>

		<b-overlay
			v-if="loading"
			:show="loading"
			rounded
			opacity="1"
			spinner-small
			spinner-variant="primary"
		>
			<div :style="`min-height: ${height}px;`"></div>
		</b-overlay>

		<!-- Buttons to toggle between different properties (datasets) -->
		<template v-if="!loading">
			<StatisticGraph
				v-if="chartData"
				:chart-options="chartOptions"
				:chart-data="chartData"
				:height="height"
			></StatisticGraph>
		</template>
	</div>
</template>

<script>
	import { toShortMillions } from "@/mixins/textFilters";
	import StatisticGraph from '@components/charts/StatisticGraph';
	import { API_GET_MOTIVATION_LOAD_USER_ORDERS_FOR_CHARTS } from '@/constants';

	export default {
		components: {
			StatisticGraph
		},
		mixins: [ toShortMillions ],	
		props: {
			selectedPeriod: {
				type: String,
				required: true
			},
			depName: {
				type: String,
				required: true
			},
		},
		data() {
			return {
				loading: false,
				chartData: null,
				rawData: null,  // Store raw data from API
				currentDataset: 'Закрытые поставки',  // Default dataset
				availableProperties: ['Закрытые поставки', 'Бонусы', 'Маржинальная прибыль'],  // Available properties
				chartOptions: {
					scales: {
						yAxes: [
							{
								id: 'y-axis-1',
								type: 'linear',
								position: 'left',
								ticks: {
									beginAtZero: true,
									// Formatter for values in thousands
									callback: function(value) {
										return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
									}
								},
								scaleLabel: {
									display: true,
									labelString: 'Закрытые поставки'
								}
							}
						],
						xAxes: [
							{
								ticks: {
									beginAtZero: true
								}
							}
						]
					},
					tooltips: {
						mode: 'index',
						position: 'average',
						intersect: false,
						callbacks: {
							label: function(tooltipItem, data) {
								let datasetLabel = data.datasets[tooltipItem.datasetIndex].label || '';
								let value = tooltipItem.yLabel.toString();
								let formattedValue = value.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
								return datasetLabel + ': ' + formattedValue;
							}
						}
					},
					maintainAspectRatio: false,
					responsive: true
				},
				height: 250,
			};
		},
		mounted() {
			this.fetchData();
		},
		methods: {
			// Fetch data from API
			fetchData() {
				this.loading = true;

				api.call('get', `${API_GET_MOTIVATION_LOAD_USER_ORDERS_FOR_CHARTS}?date_from=${this.selectedPeriod}&dep_name=${this.depName}`)
					.then((response) => {
						this.rawData = response.data;
						this.updateChartData();
						this.loading = false;
					});
			},

			// Set dataset based on button click
			setDataset(property) {
				this.loading = true;
				this.currentDataset = property;

				this.resetChart();  // Reset chart before updating
				this.updateChartData();

				setTimeout(() => {
					this.loading = false;
				}, 1000);
			},

			// Update the chart data based on the selected property
			updateChartData() {
				if (!this.rawData) return;

				let labels = [];
				let datasets = [];

				// Collect all unique dates for X-axis labels
				Object.values(this.rawData).forEach(user => {
					user.data.forEach(entry => {
						if (!labels.includes(entry.date)) {
							labels.push(entry.date);
						}
					});
				});

				// Ensure the dates are sorted
				labels.sort((a, b) => new Date(a) - new Date(b));

				// Create datasets based on the selected property
				Object.entries(this.rawData).forEach(([userId, user], index) => {
					let datasetData = labels.map(date => {
						let entry = user.data.find(d => d.date === date);
						return entry ? Number(entry[this.currentDataset]) : 0;
					});

					// Create the dataset for the selected metric (based on button clicked)
					datasets.push({
						label: `${user.name}`,
						borderColor: this.getRandomColor(index),
						fill: false,
						data: datasetData,
						yAxisID: 'y-axis-1'
					});
				});

				// Update the Y-axis label dynamically
				this.chartOptions.scales.yAxes[0].scaleLabel.labelString = this.currentDataset;

				// Set chartData for the chart
				this.chartData = {
					type: 'Line',
					labels,  // Months for x-axis
					datasets
				};
			},

			// Helper function to reset the chart
			resetChart() {
				this.chartData = null;  // Reset chart data to prevent issues
			},

			// Helper function to generate random colors for each line
			getRandomColor(index) {
				let colors = ['#C223CB', '#4A6DA0', '#A22336', '#352D52', '#F8C01A', '#4D964E', '#7561FD', '#58285E', '#126347', '#EA7B27', '#808080', '#808000', '#008000', '#D96F27', '#008080', '#aba778', '#000080', '#9e9e9e', '#FF0000', '#126347', '#EA7B27', '#808080', '#808000', '#008000', '#D96F27', '#008080', '#aba778', '#000080', '#9e9e9e'];
				let color = '#';

				if (index > (colors.length - 1)) {
					const letters = '0123456789ABCDEF';
					
					for (let i = 0; i < 6; i++) {
						color += letters[Math.floor(Math.random() * 16)];
					}
				} else {
					color = colors[index];
				}

				return color;
			}
		}
	};
</script>

<style>
.dataset-buttons {
	position: absolute;
	right: 0;
	top: -44px;
}
</style>
